import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../hooks/useAuth';
import { studentAnalyticsService } from '../../services/studentAnalyticsService';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ProgressVelocityChart } from './components/ProgressVelocityChart';
import { ContentMasteryBreakdown } from './components/ContentMasteryBreakdown';
import { StrengthsAnalysis } from './components/StrengthsAnalysis';
import { LearningTrends } from './components/LearningTrends';

/**
 * Student Personal Analytics Dashboard
 * Comprehensive learning insights with visual progress tracking
 */
export default function StudentPersonalAnalyticsDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('daily'); // daily, weekly, monthly

  useEffect(() => {
    if (user?.id) {
      loadAnalytics();
    }
  }, [user?.id]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await studentAnalyticsService?.getCompleteAnalytics(user?.id);
      setAnalytics(data);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Personal Analytics - RoleCall</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-[#6e32b9] to-[#32215d]">
          <Header />
          <div className="flex items-center justify-center h-[calc(100vh-80px)]">
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Personal Analytics - RoleCall</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-[#6e32b9] to-[#32215d]">
          <Header />
          <div className="container mx-auto px-4 py-8">
            <div className="bg-red-900/20 border border-red-500 rounded-xl p-6 text-center">
              <div className="text-red-400 text-lg mb-2">Error Loading Analytics</div>
              <div className="text-gray-300">{error}</div>
              <button
                onClick={loadAnalytics}
                className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Personal Analytics Dashboard - RoleCall</title>
        <meta name="description" content="Track your learning progress, performance patterns, and mastery trends with comprehensive visual analytics" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-[#6e32b9] to-[#32215d]">
        <Header />
        
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-2">
              Your Personal Analytics 📊
            </h1>
            <p className="text-gray-300 text-lg">
              Track your progress, identify strengths, and optimize your learning journey
            </p>
          </div>

          {/* Main Analytics Grid */}
          <div className="space-y-6">
            {/* Progress Velocity Section */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">Learning Velocity 🚀</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Track your XP accumulation and learning speed over time
                  </p>
                </div>
                <div className="flex gap-2">
                  {['daily', 'weekly', 'monthly']?.map(range => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        timeRange === range
                          ? 'bg-purple-600 text-white' :'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {range?.charAt(0)?.toUpperCase() + range?.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <ProgressVelocityChart 
                xpEvents={analytics?.progressVelocity} 
                timeRange={timeRange}
              />
            </div>

            {/* Two Column Layout for Mastery and Strengths */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Content Mastery */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-white">Content Mastery 🎭</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Performance breakdown by musicals and lesson types
                  </p>
                </div>
                <ContentMasteryBreakdown lessons={analytics?.contentMastery} />
              </div>

              {/* Strengths Analysis */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-white">Your Strengths 💪</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Identify your top-performing areas and improvement opportunities
                  </p>
                </div>
                <StrengthsAnalysis strengthsData={analytics?.strengthsAnalysis} />
              </div>
            </div>

            {/* Learning Trends Section */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white">Learning Trends 📈</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Historical progress, milestones, and predictive insights
                </p>
              </div>
              <LearningTrends trendsData={analytics?.learningTrends} />
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-400/30">
              <h3 className="text-xl font-bold text-white mb-4">Take Action 🎯</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={() => window.location.href = '/student-songs-discovery'}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-all text-left group"
                >
                  <div className="text-2xl mb-2">🎵</div>
                  <div className="text-white font-semibold group-hover:text-purple-300 transition-colors">
                    Discover New Songs
                  </div>
                  <div className="text-gray-400 text-sm mt-1">
                    Explore content to improve weaker areas
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/the-stage-student-dashboard'}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-all text-left group"
                >
                  <div className="text-2xl mb-2">📚</div>
                  <div className="text-white font-semibold group-hover:text-purple-300 transition-colors">
                    Continue Learning
                  </div>
                  <div className="text-gray-400 text-sm mt-1">
                    Resume your active lessons
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/all-badges'}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-all text-left group"
                >
                  <div className="text-2xl mb-2">🏆</div>
                  <div className="text-white font-semibold group-hover:text-purple-300 transition-colors">
                    View All Badges
                  </div>
                  <div className="text-gray-400 text-sm mt-1">
                    See what you can earn next
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}